import {
  collection,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { somerenAccessDb } from "./somerenAccessFirebase";

export const TA_INVENTORY_KEY = "TA_3_EGO";
const SESSION_KEY = "somerenlabs.taAccessSession.v1";

export function normalizeAccessCode(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, "");
}

function getTimestampMillis(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function isExpired(value) {
  const ms = getTimestampMillis(value);
  return Boolean(ms && ms < Date.now());
}

function validatePackageData(data, inventoryKey = TA_INVENTORY_KEY) {
  if (!data) throw new Error("ACCESS_CODE_NOT_FOUND");

  const productRights = data.productRights || {};
  const productRight = productRights[inventoryKey];

  if (data.status !== "active") throw new Error("ACCESS_CODE_INACTIVE");
  if (isExpired(data.validUntil)) throw new Error("ACCESS_CODE_EXPIRED");

  if (!productRight || productRight.enabled === false) {
    throw new Error("INVENTORY_NOT_ALLOWED");
  }

  const remainingUses = Number(productRight.remainingUses ?? 0);
  if (!Number.isFinite(remainingUses) || remainingUses <= 0) {
    throw new Error("NO_REMAINING_USES");
  }

  return {
    accessCode: data.code || "",
    type: data.type || "company-package",
    companyId: data.companyId || "",
    companyName: data.companyName || "",
    teamId: data.teamId || "",
    teamName: data.teamName || "",
    inventoryKey,
    remainingUses,
    totalUses: Number(productRight.totalUses ?? 0),
    usedUses: Number(productRight.usedUses ?? 0),
    validUntil: data.validUntil || null,
    status: data.status,
  };
}

async function validateParticipantCode(accessCode, data, inventoryKey = TA_INVENTORY_KEY) {
  if (data.status !== "unused") throw new Error("ACCESS_CODE_ALREADY_USED");
  if (isExpired(data.validUntil)) throw new Error("ACCESS_CODE_EXPIRED");

  const codeInventoryKey = String(data.inventoryKey || "").trim().toUpperCase();
  if (codeInventoryKey !== inventoryKey) throw new Error("INVENTORY_NOT_ALLOWED");

  const parentPackageCode = normalizeAccessCode(data.parentPackageCode);
  if (!parentPackageCode) throw new Error("PARENT_PACKAGE_NOT_FOUND");

  const parentRef = doc(somerenAccessDb, "accessCodes", parentPackageCode);
  const parentSnap = await getDoc(parentRef);
  if (!parentSnap.exists()) throw new Error("PARENT_PACKAGE_NOT_FOUND");

  const parentData = { ...parentSnap.data(), code: parentPackageCode };
  const packageAccess = validatePackageData(parentData, inventoryKey);

  return {
    ...packageAccess,
    accessCode,
    type: "participant-single-use",
    parentPackageCode,
    participantCode: accessCode,
    status: data.status,
  };
}

export async function validateAccessCode(rawAccessCode, inventoryKey = TA_INVENTORY_KEY) {
  const accessCode = normalizeAccessCode(rawAccessCode);
  if (!accessCode) throw new Error("EMPTY_ACCESS_CODE");

  const accessRef = doc(somerenAccessDb, "accessCodes", accessCode);
  const snap = await getDoc(accessRef);

  if (!snap.exists()) throw new Error("ACCESS_CODE_NOT_FOUND");

  const data = { ...snap.data(), code: accessCode };

  const access = data.type === "participant-single-use"
    ? await validateParticipantCode(accessCode, data, inventoryKey)
    : validatePackageData(data, inventoryKey);

  storeAccessSession(access);
  return access;
}

export function storeAccessSession(access) {
  if (!access?.accessCode) return;
  window.sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({
      ...access,
      storedAt: new Date().toISOString(),
    })
  );
}

export function getStoredAccessSession() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getAccessCodeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return normalizeAccessCode(params.get("slAccessCode"));
}

export async function completeInventoryUsage({
  accessCode: rawAccessCode,
  inventoryKey = TA_INVENTORY_KEY,
  resultId,
  participantName,
  language,
}) {
  const accessCode = normalizeAccessCode(rawAccessCode);
  const normalizedInventoryKey = String(inventoryKey || "").trim().toUpperCase();

  if (!accessCode) throw new Error("EMPTY_ACCESS_CODE");
  if (!normalizedInventoryKey) throw new Error("EMPTY_INVENTORY_KEY");
  if (!resultId) throw new Error("EMPTY_RESULT_ID");

  const accessRef = doc(somerenAccessDb, "accessCodes", accessCode);
  const logRef = doc(collection(somerenAccessDb, "globalUsageLogs"));

  return runTransaction(somerenAccessDb, async (transaction) => {
    const accessSnap = await transaction.get(accessRef);
    if (!accessSnap.exists()) throw new Error("ACCESS_CODE_NOT_FOUND");

    const accessData = { ...accessSnap.data(), code: accessCode };
    const isParticipantCode = accessData.type === "participant-single-use";

    const parentPackageCode = isParticipantCode
      ? normalizeAccessCode(accessData.parentPackageCode)
      : accessCode;

    if (!parentPackageCode) throw new Error("PARENT_PACKAGE_NOT_FOUND");

    const packageRef = doc(somerenAccessDb, "accessCodes", parentPackageCode);
    const packageSnap = isParticipantCode
      ? await transaction.get(packageRef)
      : accessSnap;

    if (!packageSnap.exists()) throw new Error("PARENT_PACKAGE_NOT_FOUND");

    const packageData = { ...packageSnap.data(), code: parentPackageCode };
    const packageAccess = validatePackageData(packageData, normalizedInventoryKey);

    if (isParticipantCode) {
      if (accessData.status === "completed") {
        return { alreadyConsumed: true };
      }

      if (accessData.status !== "unused") {
        throw new Error("ACCESS_CODE_ALREADY_USED");
      }

      if (isExpired(accessData.validUntil)) {
        throw new Error("ACCESS_CODE_EXPIRED");
      }

      const codeInventoryKey = String(accessData.inventoryKey || "").trim().toUpperCase();
      if (codeInventoryKey !== normalizedInventoryKey) {
        throw new Error("INVENTORY_NOT_ALLOWED");
      }
    }

    const consumptionRef = doc(
      collection(packageRef, "consumptions"),
      `${normalizedInventoryKey}_${resultId}`
    );

    const consumptionSnap = await transaction.get(consumptionRef);
    if (consumptionSnap.exists()) {
      return { alreadyConsumed: true };
    }

    const productRight = packageData.productRights?.[normalizedInventoryKey] || {};
    const remainingUses = Number(productRight.remainingUses ?? 0);
    const usedUses = Number(productRight.usedUses ?? 0);
    const packageRemainingUses = Number(packageData.remainingUses ?? 0);
    const packageUsedUses = Number(packageData.usedUses ?? 0);

    if (!Number.isFinite(remainingUses) || remainingUses <= 0) {
      throw new Error("NO_REMAINING_USES");
    }

    transaction.update(packageRef, {
      [`productRights.${normalizedInventoryKey}.remainingUses`]: Math.max(0, remainingUses - 1),
      [`productRights.${normalizedInventoryKey}.usedUses`]: usedUses + 1,
      remainingUses: Math.max(0, packageRemainingUses - 1),
      usedUses: packageUsedUses + 1,
      lastUsedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    if (isParticipantCode) {
      transaction.update(accessRef, {
        status: "completed",
        completedAt: serverTimestamp(),
        resultId,
        participantName: participantName || "",
        updatedAt: serverTimestamp(),
      });
    }

    transaction.set(consumptionRef, {
      accessCode,
      parentPackageCode,
      inventoryKey: normalizedInventoryKey,
      resultId,
      source: "ta-3-ego",
      participantName: participantName || "",
      language: language || "",
      consumedAt: serverTimestamp(),
    });

    transaction.set(logRef, {
      eventType: "inventory_completed",
      source: "ta-3-ego",
      accessCode,
      parentPackageCode,
      inventoryKey: normalizedInventoryKey,
      resultId: resultId || "",
      participantName: participantName || "",
      language: language || "",
      companyId: packageAccess.companyId || "",
      companyName: packageAccess.companyName || "",
      teamId: packageAccess.teamId || "",
      teamName: packageAccess.teamName || "",
      createdAt: serverTimestamp(),
    });

    return {
      ...packageAccess,
      alreadyConsumed: false,
      remainingUses: Math.max(0, remainingUses - 1),
      usedUses: usedUses + 1,
    };
  });
}

export function getAccessErrorMessage(error, language = "tr") {
  const key = error?.message || "UNKNOWN";
  const messages = {
    tr: {
      EMPTY_ACCESS_CODE: "Lütfen erişim kodunu girin.",
      ACCESS_CODE_NOT_FOUND: "Erişim kodu bulunamadı.",
      ACCESS_CODE_INACTIVE: "Bu erişim kodu aktif değil.",
      ACCESS_CODE_EXPIRED: "Bu erişim kodunun süresi dolmuş.",
      ACCESS_CODE_ALREADY_USED: "Bu erişim kodu daha önce kullanılmış.",
      PARENT_PACKAGE_NOT_FOUND: "Bu katılımcı kodunun şirket paketi bulunamadı.",
      INVENTORY_NOT_ALLOWED: "Bu kod TA (3 EGO) envanteri için yetkili değil.",
      NO_REMAINING_USES: "Bu paket için TA (3 EGO) kullanım hakkı kalmamış.",
      UNKNOWN: "Erişim doğrulanamadı. Lütfen tekrar deneyin.",
    },
    en: {
      EMPTY_ACCESS_CODE: "Please enter an access code.",
      ACCESS_CODE_NOT_FOUND: "Access code was not found.",
      ACCESS_CODE_INACTIVE: "This access code is not active.",
      ACCESS_CODE_EXPIRED: "This access code has expired.",
      ACCESS_CODE_ALREADY_USED: "This access code has already been used.",
      PARENT_PACKAGE_NOT_FOUND: "The company package for this participant code was not found.",
      INVENTORY_NOT_ALLOWED: "This code is not authorized for TA (3 EGO).",
      NO_REMAINING_USES: "No TA (3 EGO) usage rights remain for this package.",
      UNKNOWN: "Access could not be confirmed. Please try again.",
    },
  };

  return messages[language]?.[key] || messages[language]?.UNKNOWN || messages.tr.UNKNOWN;
}
